import React from "react";
import styles from "./index.module.css";
import { Button, Form, Grid, Loader } from "semantic-ui-react";
import agent from "../../agent";
import { Appointment } from "../dashboard";
import {Link, Redirect, RouteComponentProps} from "react-router-dom";
import { GeneralTitleCase, getGeneralTitle } from "../../utils/common";
import { hasRole, UserRole } from "../../helpers/UserRole";
import SlateEditableText from "../general/SlateEditableText/SlateEditableText";
import {slateToHtml, htmlToSlate, isSlateContentEmpty} from "../general/SlateEditableText/SlateHelpers";
import { Descendant } from "slate";
import {EMPTY_SLATE_VALUE} from "../../consts/EventConsts";
import {extractReturnUrl, handleGoBack} from "../../helpers/TablePageUtils";
import { textLabels } from "../../consts/textLabels";
import { EVENT_DESCRIPTION_MAX_LENGTH } from "../../consts/PatientConsts";

interface MatchParams {
    id: string;
}

interface Props extends RouteComponentProps<MatchParams> {}

interface State {
    editable: boolean;
    _id: string;
    name: Descendant[];
    loading: boolean;
    saved: boolean;
    editMode: boolean;
    returnUrl: string;
}

class AppointmentEditor extends React.PureComponent<Props, State> {
    private readonly FALLBACK_URL = "/dashboard/appointments";

    state: State = {
        editable: false,
        _id: '',
        name: EMPTY_SLATE_VALUE,
        loading: false,
        editMode: false,
        saved: false,
        returnUrl: this.FALLBACK_URL,
    };

    async componentDidMount() {
        const extractedUrl = extractReturnUrl(this.props.location, this.FALLBACK_URL);
        this.setState({ returnUrl: extractedUrl });
        if (this.props.match && this.props.match.path.includes('edit')) {
            this.setState({ editMode: true, loading: true });
            await this.onMount();
        } else {
            this.setState({ editable: true });
        }
    }

    onMount = async (): Promise<void> => {
        const appointmentId: string = this.props.match ? this.props.match.params.id : '';
        const doc: Appointment = await agent.AppointmentProvider.open(appointmentId);

        if (doc) {
            this.setState({
                _id: doc._id,
                name: htmlToSlate(doc.name),
                loading: false,
            });
        }
    };

    // htmlToSlate = (html: string): Descendant[] => {
    //     const parser = new DOMParser();
    //     const doc = parser.parseFromString(html, 'text/html');
    //     const body = doc.body;
    //
    //     const traverseNode = (node: Node): Descendant[] => {
    //         if (node.nodeType === Node.TEXT_NODE) {
    //             return [{ text: node.textContent || '' }];
    //         }
    //
    //         const children: Descendant[] = [];
    //         for (const child of node.childNodes) {
    //             children.push(...traverseNode(child));
    //         }
    //
    //         switch (node.nodeName) {
    //             case 'P':
    //                 return [{ type: 'paragraph', children }];
    //             case 'H1':
    //                 return [{ type: 'heading', children }];
    //             case 'STRONG':
    //                 return [{ text: node.textContent || '', bold: true }];
    //             case 'EM':
    //                 return [{ text: node.textContent || '', italic: true }];
    //             case 'U':
    //                 return [{ text: node.textContent || '', underline: true }];
    //             default:
    //                 return children;
    //         }
    //     };
    //
    //     return traverseNode(body);
    // };

    onCreate = async (): Promise<void> => {
        this.setState({ loading: true });

        await agent.AppointmentProvider.create({
            _id: '',
            name: slateToHtml(this.state.name),
        });

        this.setState({ loading: false, saved: true });
    };

    onEdit = async () => {
        this.setState({ loading: true });

        await agent.AppointmentProvider.edit({
            _id: this.state._id,
            name: slateToHtml(this.state.name),
        }).then((doc: Appointment) => {
            this.setState({
                _id: doc._id,
                name: htmlToSlate(doc.name),
                editable: false,
            });
        });

        this.setState({ loading: false, saved: true });
    };

    isSaveDisabled = (): boolean => {
        return isSlateContentEmpty(this.state.name)
    };

    handleGoBack = (e: React.MouseEvent) => {
        e.preventDefault();
        handleGoBack(this.props.history, this.state.returnUrl, this.FALLBACK_URL);
    };

    render() {
        const entityLabel = getGeneralTitle(GeneralTitleCase.appointmentsa);
        const headerLabel: string = this.state.editMode ? `Данные ${entityLabel}` : `Создание ${entityLabel}`;

        return (
            this.state.saved ?
                <Redirect to={this.state.returnUrl || this.FALLBACK_URL} />
                :
                <div className={styles.appointmentInfoContent}>
                    <div className={styles.appointmentInfoHeader}>
                        {headerLabel}
                    </div>
                    {
                        this.state.loading ?
                            <Loader active />
                            :
                            <Form>
                                <Grid>
                                    <Grid.Column width={4}>
                                        <label>
                                            Наименование {entityLabel}<i
                                            className="info circle icon grey"
                                            title="Для применения параметров форматирования необходимо: выделить часть текста и нажать Ctrl+B для выделения жирным, Ctrl+I для выделения курсивом, Ctrl+U для подчёркивания текста."></i>
                                        </label>
                                        <SlateEditableText
                                            value={this.state.name} // Передаем массив Descendant
                                            onChange={(newDescription: Descendant[]) =>
                                                this.setState({
                                                    name: newDescription
                                                })
                                            }
                                            maxLength={EVENT_DESCRIPTION_MAX_LENGTH}
                                            disabled={hasRole(UserRole.OBSERVER) || !this.state.editable}
                                        />
                                    </Grid.Column>

                                    <Grid.Column width={3}>
                                        <div className={styles.appointmentEditorButtons}>

                                            {
                                                this.state.editable ?
                                                    <Button
                                                        className={styles.button}
                                                        color={'green'}
                                                        type={'submit'}
                                                        content={textLabels.saveButton}
                                                        disabled={this.isSaveDisabled()}
                                                        onClick={this.state.editMode ? this.onEdit : this.onCreate}
                                                    />
                                                    :
                                                    <Button
                                                        className={styles.button}
                                                        color={'teal'}
                                                        type={'submit'}
                                                        content={textLabels.editButton}
                                                        onClick={() => this.setState({ editable: true })}
                                                    />
                                            }

                                            <Button
                                                className={styles.button}
                                                color="grey"
                                                type="button"
                                                content={textLabels.returnButton}
                                                onClick={this.handleGoBack}
                                            />

                                        </div>
                                    </Grid.Column>
                                </Grid>
                            </Form>
                    }
                </div>
        )
    }
}

export default AppointmentEditor;